import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, Phone, Trash2, X } from 'lucide-react';
import { complaintAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import ImageLightbox from '../ImageLightbox';
import Select from '../Select';
import { authedMediaUrl } from '../../utils/media';
import { formatClinicDate, formatClinicDateTime } from '../../utils/clinicTime';
import ComplaintTimeline from './ComplaintTimeline';
import {
  CHANNEL, CONTACT_OUTCOME, NEEDS_NOTE, NEEDS_OUTCOME, NEXT_STATUSES, OUTCOME, OUTCOMES_FOR,
  SEVERITY, STATUS, categoryLabel, isOverdue, isWrittenPage, messageOf, optionsFrom, statusOf,
} from './complaintStatus';

// One complaint from arrival to close: who owns it, who has been rung, what was found, what was
// decided, and whether the complainant was ever told. The buttons offered depend on what the reader
// may actually do — an assignee can investigate and record, but only management decides the
// outcome, which is what "complaints go directly to the Management" means.
//
// Everything the patient typed renders with dir="auto": they write in Urdu or English as they
// please, and the browser picks the direction from what they actually wrote.

const Row = ({ label, children }) => (
  <div className="flex gap-2">
    <dt className="w-28 flex-shrink-0 text-gray-500">{label}</dt>
    <dd className="text-gray-900 min-w-0">{children}</dd>
  </div>
);

const Section = ({ title, children, action }) => (
  <section className="border-t border-gray-200 pt-4">
    <div className="flex items-center justify-between gap-2 mb-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

// Declared out here on purpose: a component defined inside the body is a new type on every render,
// so React would unmount and remount it and the textarea would lose focus after every keystroke.
const Panel = ({ title, onSave, saveLabel, onCancel, saving, error, children }) => (
  <div className="rounded-xl border border-gray-300 bg-gray-50 p-3 space-y-3">
    <p className="text-sm font-semibold text-gray-900">{title}</p>
    {children}
    {error && <p className="text-sm text-red-700">{error}</p>}
    <div className="flex gap-2">
      <button type="button" onClick={onCancel} disabled={saving} className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
        Cancel
      </button>
      <button type="button" onClick={onSave} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
        {saving ? 'Saving…' : saveLabel}
      </button>
    </div>
  </div>
);

const ComplaintDetailModal = ({
  complaint: initial, canEdit, canDelete, canAssign, onChanged, onDeleted, onClose,
}) => {
  const notify = useToast();
  const confirm = useConfirm();
  const [complaint, setComplaint] = useState(initial);
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [panel, setPanel] = useState(null); // 'contact' | 'note' | 'assign' | 'triage' | 'informed' | a status
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(false);
  const dialogRef = useDialog(() => { if (!saving && !zoom) onClose(); }, { confirmDiscard: false });

  const chip = statusOf(complaint);
  const photo = complaint.photoUrl ? authedMediaUrl(complaint.photoUrl) : null;
  const overdue = isOverdue(complaint);
  const closed = ['RESOLVED', 'DISMISSED'].includes(complaint.status);

  // The list sends a summary; the full record and its history come from the server when opened, so
  // the timeline is never a guess assembled in the browser.
  const load = useCallback(async () => {
    try {
      const full = await complaintAPI.get(initial.id);
      setComplaint(full);
      setEvents(full.timeline || []);
    } catch {
      // The summary we were handed is still worth showing; only the history is missing.
    }
  }, [initial.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canAssign) return;
    complaintAPI.assignable().then(setStaff).catch(() => setStaff([]));
  }, [canAssign]);

  const open = (which, initialForm = {}) => { setPanel(which); setForm(initialForm); setError(''); };
  const close = () => { setPanel(null); setForm({}); setError(''); };

  const after = (saved, message) => {
    setComplaint(saved);
    setEvents(saved.timeline || []);
    close();
    notify('success', message);
    onChanged(saved);
  };

  const run = async (fn, message) => {
    setSaving(true);
    setError('');
    try {
      after(await fn(), message);
    } catch (e) {
      setError(e?.message || 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const moveTo = (status) => {
    if (NEEDS_NOTE.includes(status) && !(form.note || '').trim()) {
      setError('Write what was done about it before closing it.');
      return;
    }
    if (NEEDS_OUTCOME.includes(status) && !form.outcome) {
      setError('Choose what was decided.');
      return;
    }
    run(
      () => complaintAPI.update(complaint.id, {
        status,
        resolutionNote: (form.note || '').trim() || null,
        outcome: form.outcome || null,
      }),
      `Marked ${STATUS[status].label.toLowerCase()}`,
    );
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Delete this complaint?',
      message: 'Use this for spam only. A genuine complaint should be dismissed with a reason instead, so the record of it stays.',
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await complaintAPI.remove(complaint.id);
      notify('success', 'Complaint deleted');
      onDeleted(complaint.id);
    } catch (e) {
      notify('error', e?.message || 'The complaint could not be deleted.');
    }
  };

  const noteBox = (placeholder, label) => (
    <textarea
      value={form.note || ''}
      onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
      rows={3}
      autoFocus
      aria-label={label}
      placeholder={placeholder}
      dir="auto"
      className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
    />
  );

  return (
    <>
      <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label={`Complaint ${complaint.reference}`}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col">
          <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-start justify-between gap-3 rounded-t-2xl">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-white font-mono">{complaint.reference}</h2>
              <p className="text-sm text-navy-100">{categoryLabel(complaint)} · {formatClinicDateTime(complaint.createdAt)}</p>
            </div>
            <button type="button" aria-label="Close" onClick={onClose} disabled={saving} className="p-2 hover:bg-white/20 rounded-lg flex-shrink-0">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${chip.chip}`}>{chip.label}</span>
              {complaint.severity && (
                <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${SEVERITY[complaint.severity].chip}`}>
                  {SEVERITY[complaint.severity].label}
                </span>
              )}
              {overdue && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-800">
                  <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Overdue
                </span>
              )}
              {complaint.outcome && (
                <span className="inline-flex rounded-full bg-navy-100 px-3 py-1 text-sm font-semibold text-navy-800">
                  {OUTCOME[complaint.outcome]?.label || complaint.outcome}
                </span>
              )}
            </div>

            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
              {messageOf(complaint) ? (
                <p className="whitespace-pre-wrap text-gray-900 leading-relaxed" dir="auto">{complaint.message}</p>
              ) : (
                <p className="text-gray-600 italic">
                  {isWrittenPage(complaint)
                    ? 'They wrote this complaint out by hand and photographed it. The page is below.'
                    : 'Nothing was written.'}
                </p>
              )}
            </div>

            {photo && (
              <button type="button" onClick={() => setZoom(true)} className="block w-full rounded-xl overflow-hidden border border-gray-200">
                <img
                  src={photo}
                  alt={isWrittenPage(complaint) ? 'The complaint, written by hand' : 'Sent with the complaint'}
                  // A written page is the complaint itself, so it is shown whole rather than cropped
                  // to a neat strip; anything else hides the half of it that matters.
                  className={`w-full ${isWrittenPage(complaint) ? 'object-contain max-h-[60vh] bg-gray-50' : 'max-h-64 object-cover'}`}
                />
              </button>
            )}

            <dl className="text-sm space-y-1.5">
              <Row label="From"><span dir="auto">{complaint.complainantName}</span></Row>
              <Row label="Phone">
                <a href={`tel:${complaint.phone}`} className="inline-flex items-center gap-1.5 text-navy-700 hover:underline">
                  <Phone className="w-3.5 h-3.5" aria-hidden="true" /> {complaint.phone}
                </a>
              </Row>
              {complaint.address && <Row label="Address"><span dir="auto">{complaint.address}</span></Row>}
              {complaint.patientMatch && (
                <Row label="On file">
                  <span className="inline-flex items-center gap-1.5 text-gray-700">
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                    {complaint.patientMatch.fullName}
                    <span className="font-mono text-xs text-gray-500">{complaint.patientMatch.mrn}</span>
                  </span>
                  <span className="block text-xs text-gray-400">Same phone number as this patient. They typed it themselves, so check before relying on it.</span>
                </Row>
              )}
            </dl>

            {/* ---- who is dealing with it ---- */}
            <Section
              title="Handling"
              action={canEdit && !closed ? (
                <button type="button" onClick={() => open('triage', { severity: complaint.severity || '', concernsUserId: complaint.concernsUserId || '' })} className="text-xs font-medium text-navy-700 hover:underline">
                  Change
                </button>
              ) : null}
            >
              <dl className="text-sm space-y-1.5">
                <Row label="Assigned to">
                  {complaint.assignedToName ? (
                    <>
                      {complaint.assignedToName}
                      <span className="block text-xs text-gray-400">
                        by {complaint.assignedByName} on {formatClinicDateTime(complaint.assignedAt)}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-500">Nobody yet</span>
                  )}
                  {canAssign && !closed && (
                    <button type="button" onClick={() => open('assign', { userId: complaint.assignedToId || '' })} className="ml-2 text-xs font-medium text-navy-700 hover:underline">
                      {complaint.assignedToName ? 'Reassign' : 'Assign'}
                    </button>
                  )}
                </Row>
                <Row label="Severity">
                  {complaint.severity ? SEVERITY[complaint.severity].label : <span className="text-gray-500">Not set</span>}
                </Row>
                {/* Who it concerns is never shown to the person named; only a complaints holder sees it. */}
                <Row label="Concerns">
                  {complaint.concernsUserName || <span className="text-gray-500">Nobody named</span>}
                </Row>
                {complaint.dueAt && !closed && (
                  <Row label="Due">
                    <span className={overdue ? 'text-red-700 font-medium' : ''}>
                      {formatClinicDate(complaint.dueAt)}
                    </span>
                    {complaint.status === 'AWAITING_COMPLAINANT' && (
                      <span className="block text-xs text-gray-400">Paused while waiting on the complainant.</span>
                    )}
                  </Row>
                )}
                {closed && (
                  <Row label="Complainant">
                    {complaint.complainantInformedAt ? (
                      <>
                        Told on {formatClinicDateTime(complaint.complainantInformedAt)}
                        <span className="block text-xs text-gray-400">by {complaint.complainantInformedByName}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-amber-700">Not told yet</span>
                        {canEdit && (
                          <button type="button" onClick={() => open('informed', { channel: 'PHONE' })} className="ml-2 text-xs font-medium text-navy-700 hover:underline">
                            Record that they were told
                          </button>
                        )}
                      </>
                    )}
                  </Row>
                )}
              </dl>
            </Section>

            {/* ---- the record ---- */}
            <Section title="What has happened">
              <ComplaintTimeline events={events} />
            </Section>

            {/* ---- the panels ---- */}
            {panel === 'contact' && (
              <Panel
                onCancel={close} saving={saving} error={error}
                title="Log a call"
                saveLabel="Save"
                onSave={() => {
                  if (!form.contactOutcome) { setError('Choose what happened when you tried.'); return; }
                  run(() => complaintAPI.addEvent(complaint.id, {
                    type: 'CONTACT',
                    channel: form.channel || 'PHONE',
                    contactOutcome: form.contactOutcome,
                    note: (form.note || '').trim() || null,
                  }), 'Call recorded');
                }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Select label="How" value={form.channel || 'PHONE'} onChange={(v) => setForm((f) => ({ ...f, channel: v }))} options={optionsFrom(CHANNEL)} />
                  <Select label="What happened" value={form.contactOutcome || ''} onChange={(v) => setForm((f) => ({ ...f, contactOutcome: v }))} options={optionsFrom(CONTACT_OUTCOME)} placeholder="Choose…" />
                </div>
                {noteBox('What they said, if you reached them', 'What they said')}
              </Panel>
            )}

            {panel === 'note' && (
              <Panel
                onCancel={close} saving={saving} error={error}
                title="Add a note"
                saveLabel="Save"
                onSave={() => {
                  if (!(form.note || '').trim()) { setError('Write the note first.'); return; }
                  run(() => complaintAPI.addEvent(complaint.id, { type: 'NOTE', note: form.note.trim() }), 'Note added');
                }}
              >
                {noteBox('What you found', 'What you found')}
                <p className="text-xs text-gray-500">Internal. The complainant never sees this.</p>
              </Panel>
            )}

            {panel === 'assign' && (
              <Panel
                onCancel={close} saving={saving} error={error}
                title={complaint.assignedToName ? 'Reassign this complaint' : 'Assign this complaint'}
                saveLabel="Save"
                onSave={() => run(
                  () => complaintAPI.assign(complaint.id, { userId: form.userId || null, note: (form.note || '').trim() || null }),
                  form.userId ? 'Assigned' : 'Unassigned',
                )}
              >
                <Select
                  label="Who should look into it"
                  value={form.userId || ''}
                  onChange={(v) => setForm((f) => ({ ...f, userId: v }))}
                  options={[{ value: '', label: 'Nobody' }, ...staff.map((u) => ({ value: u.id, label: `${u.fullName} · ${u.role}` }))]}
                  placeholder="Choose…"
                />
                <p className="text-xs text-gray-500">
                  They will see this one complaint and no others, whether or not they can otherwise open Complaints.
                </p>
                {noteBox('Why, or what you want them to check', 'Why')}
              </Panel>
            )}

            {panel === 'triage' && (
              <Panel
                onCancel={close} saving={saving} error={error}
                title="Severity and who it concerns"
                saveLabel="Save"
                onSave={() => run(() => complaintAPI.triage(complaint.id, {
                  severity: form.severity || null,
                  concernsUserId: form.concernsUserId || null,
                }), 'Saved')}
              >
                <Select
                  label="How serious"
                  value={form.severity || ''}
                  onChange={(v) => setForm((f) => ({ ...f, severity: v }))}
                  options={Object.entries(SEVERITY).map(([k, v]) => ({ value: k, label: `${v.label} · answer within ${v.days} day${v.days === 1 ? '' : 's'}` }))}
                  placeholder="Choose…"
                />
                <Select
                  label="Who it concerns"
                  value={form.concernsUserId || ''}
                  onChange={(v) => setForm((f) => ({ ...f, concernsUserId: v }))}
                  options={[{ value: '', label: 'Nobody in particular' }, ...staff.map((u) => ({ value: u.id, label: `${u.fullName} · ${u.role}` }))]}
                  placeholder="Nobody in particular"
                />
                <p className="text-xs text-gray-500">
                  Naming somebody does not count against them. Upheld and not-upheld complaints are counted
                  separately, and the person named is never shown this.
                </p>
              </Panel>
            )}

            {panel === 'informed' && (
              <Panel
                onCancel={close} saving={saving} error={error}
                title="Record that the complainant was told"
                saveLabel="Save"
                onSave={() => run(() => complaintAPI.informed(complaint.id, {
                  channel: form.channel || 'PHONE',
                  note: (form.note || '').trim() || null,
                }), 'Recorded')}
              >
                <Select label="How" value={form.channel || 'PHONE'} onChange={(v) => setForm((f) => ({ ...f, channel: v }))} options={optionsFrom(CHANNEL)} />
                {noteBox('What they were told', 'What they were told')}
              </Panel>
            )}

            {panel && STATUS[panel] && (
              <Panel
                onCancel={close} saving={saving} error={error}
                title={`Mark ${STATUS[panel].label.toLowerCase()}`}
                saveLabel={`Mark ${STATUS[panel].label.toLowerCase()}`}
                onSave={() => moveTo(panel)}
              >
                {NEEDS_OUTCOME.includes(panel) && (
                  <Select
                    label="What was decided"
                    value={form.outcome || ''}
                    onChange={(v) => setForm((f) => ({ ...f, outcome: v }))}
                    options={OUTCOMES_FOR[panel].map((k) => ({ value: k, label: `${OUTCOME[k].label} · ${OUTCOME[k].hint}` }))}
                    placeholder="Choose…"
                  />
                )}
                {noteBox(
                  NEEDS_NOTE.includes(panel) ? 'What was done about it' : 'A note, if you want one',
                  NEEDS_NOTE.includes(panel) ? 'What was done about it' : 'A note',
                )}
              </Panel>
            )}

            {/* ---- what this reader may do ---- */}
            {!panel && (
              <div className="flex flex-wrap gap-2 pt-1">
                {!closed && (
                  <>
                    <button type="button" onClick={() => open('contact', { channel: 'PHONE' })} className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                      Log a call
                    </button>
                    <button type="button" onClick={() => open('note')} className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                      Add a note
                    </button>
                  </>
                )}
                {(NEXT_STATUSES[complaint.status] || []).map((s) => {
                  // Parking and picking up are part of investigating; closing is management's call.
                  const investigative = ['UNDER_REVIEW', 'AWAITING_COMPLAINANT'].includes(s);
                  if (!investigative && !canEdit) return null;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => open(s)}
                      className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                    >
                      {closed && s === 'UNDER_REVIEW' ? 'Reopen' : `Mark ${STATUS[s].label.toLowerCase()}`}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t bg-gray-50 flex justify-between gap-2 rounded-b-2xl">
            {canDelete ? (
              <button type="button" onClick={remove} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-300 bg-white text-red-700 rounded-lg text-sm font-medium hover:bg-red-50">
                <Trash2 className="w-4 h-4" aria-hidden="true" /> Delete as spam
              </button>
            ) : <span />}
            <button type="button" onClick={onClose} disabled={saving} className="px-5 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Done</button>
          </div>
        </div>
      </div>

      {zoom && photo && <ImageLightbox src={photo} alt="Sent with the complaint" onClose={() => setZoom(false)} />}
    </>
  );
};

export default ComplaintDetailModal;
